import { useEffect, useState } from 'react';

import {
  GroupItem,
  GroupMemberItem,
  LocationSharingApi,
} from '@deckee/api-app-main';
import { useSelector } from 'react-redux';
import apiConfiguration from '../../../../services/backend/apiConfiguration';
import { selectCurrentMemberId } from '../../../../services/store/modules/backend-service';
import { selectAllSignals } from '../../../../services/store/modules/signal';
import { Signal } from '../../../../services/store/modules/signal/models';

const api = new LocationSharingApi(apiConfiguration);

interface GroupMemberWithSignal extends GroupMemberItem {
  signal?: Signal;
}

interface GroupItemWithSignals extends GroupItem {
  members: GroupMemberWithSignal[];
}

const useGroup = (groupId: string) => {
  const [apiGroup, setApiGroup] = useState<GroupItem | null>(null);
  const currentMemberId = useSelector(selectCurrentMemberId);
  const [group, setGroup] = useState<GroupItemWithSignals | null>(null);
  const [error, setError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Get all signals from the store
  const allSignals = useSelector(selectAllSignals);

  useEffect(() => {
    if (!apiGroup) {
      return;
    }

    const membersWithSignal = apiGroup.members.map((member) => {
      const memberKey = String(member.memberId ?? '');
      const matches = allSignals.filter(
        (s) => String(s.memberid ?? '') === memberKey,
      );
      // When multiple signals match (e.g. from different fetches), pick one
      // deterministically: prefer profile (has image), then latest timestamp,
      // then stable by id so the choice doesn't flicker.
      const signal =
        matches.length === 0
          ? undefined
          : [...matches].sort((a, b) => {
              const aHasImage = Boolean(a.image);
              const bHasImage = Boolean(b.image);
              if (aHasImage !== bHasImage) return aHasImage ? -1 : 1;
              const tA = a.timestamp ?? '';
              const tB = b.timestamp ?? '';
              if (tA !== tB) return tB.localeCompare(tA);
              return String(a.id).localeCompare(String(b.id));
            })[0];
      return { ...member, signal };
    });

    const groupWithSignals: GroupItemWithSignals = {
      ...apiGroup,
      members: membersWithSignal,
    };
    setGroup(groupWithSignals);
  }, [apiGroup, allSignals, currentMemberId]);

  useEffect(() => {
    if (!groupId) {
      setIsLoading(false);
      return;
    }

    api
      .getGroup({ id: groupId })
      .then((response) => {
        if (response.success && response.data) {
          setApiGroup(response.data);
        } else {
          setError(true);
        }
      })
      .catch((error) => {
        setError(true);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [groupId]);

  return { group, error, isLoading };
};

export default useGroup;
